import React, { useState, useRef } from 'react';
import { Box, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { useMagicCodeLoginStore } from '../../stores/magicCodeLoginStore';
import { useAuthStore } from '../../stores/authStore';

interface EmailStepProps {
  onSuccess: (email: string) => void;
}

const EmailStep: React.FC<EmailStepProps> = ({ onSuccess }) => {
  const {
    email,
    loading,
    error,
    setEmail,
    setError,
    setEmailSent,
    clearError,
    goToCodeStep,
  } = useMagicCodeLoginStore();

  const { requestMagicCode } = useAuthStore();
  const [emailError, setEmailError] = useState('');
  const submittingRef = useRef(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async () => {
    // Prevent double submission (e.g., from React StrictMode in development)
    if (submittingRef.current) return;

    // Perform synchronous validation outside try block to allow safe early returns
    clearError();
    setEmailError('');

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Set guard before async operation
    submittingRef.current = true;

    try {
      const result = await requestMagicCode(email);

      if (!result.success && result.error) {
        console.error('Magic code request error:', result.error);
        setError(result.error);
        return;
      }

      // Seed the resend cooldown so CodeStep picks it up on mount
      const expiry = Date.now() + 30 * 1000;
      sessionStorage.setItem(
        `magic-code-resend-expiry-${email}`,
        String(expiry)
      );

      setEmailSent(true);
      goToCodeStep();
      onSuccess(email);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleEmailSubmit();
    }
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleEmailSubmit();
  };

  return (
    <Box data-testid="magic-code-email-step">
      <form onSubmit={handleFormSubmit} noValidate>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            data-testid="magic-code-error-alert"
          >
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          name="email"
          type="email"
          label="Email Address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!emailError}
          helperText={emailError}
          disabled={loading}
          autoFocus
          autoComplete="username webauthn"
          slotProps={{
            htmlInput: { 'data-testid': 'magic-code-email-input' },
          }}
          sx={{
            mb: 3,
            input: {
              fontSize: '1.1rem',
            },
          }}
        />

        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={loading || !email.trim()}
          data-testid="magic-code-send-button"
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            mb: 2,
            position: 'relative',
          }}
        >
          {loading ? (
            <CircularProgress
              size={24}
              color="inherit"
              data-testid="magic-code-email-loading"
            />
          ) : (
            'Login'
          )}
        </Button>

        {/* Traditional login has been removed */}
      </form>
    </Box>
  );
};

export default EmailStep;
